import Modal from "@/components/Modal";
import { useStores } from "@/hooks/useStores";
import clsx from "clsx";
import * as React from "react";
import Balancer from "react-wrap-balancer";
import { useYbugApi } from "ybug-react";
import Button from "./Button";
import InlineLoading from "./InlineLoading";

export const RecipeFeedbackBugWidget = ({ recipe }) => {
  const YbugContext = useYbugApi();
  const YbugApi = YbugContext?.Ybug;
  const [modalOpen, setModalOpen] = React.useState(false);
  const [feedback, setFeedback] = React.useState("");
  const [status, setStatus] = React.useState("initial");
  const { authStore } = useStores();
  const { currentUser } = authStore;

  React.useEffect(() => {
    const openSendRecipeFeedback = () => {
      // Click #open-send-recipe-feedback button
      const event = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: true,
      });
      const cb = document.getElementById("open-send-recipe-feedback");
      cb.dispatchEvent(event);
    };
    window.addEventListener(
      "open-send-recipe-feedback",
      openSendRecipeFeedback
    );

    return () => {
      window.removeEventListener(
        "open-send-recipe-feedback",
        openSendRecipeFeedback
      );
    };
  }, []);

  const submitFeedback = () => {
    setStatus("submitting");

    fetch("/api/recipes/send-feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        feedback,
        recipeTitle: recipe.title,
        recipeUrl: `https://www.mob.co.uk/${recipe.uri}`,
        recipeId: recipe.id,
        email: currentUser !== null ? currentUser.email : "unknown",
      }),
    })
      .then((response) => {
        if (response.status === 200) {
          setStatus("complete");
        } else {
          setStatus("initial");
        }
      })
      .catch((error) => {
        setStatus("error");
      });
  };

  return (
    <div className={clsx("@container")}>
      <div className="relative space-y-4">
        <div className="space-y-2">
          <h3 className="text-base @lg:text-lg @xl:text-xl">
            <span>Get in Touch</span>
          </h3>
          <div className={clsx("body-text-sm")}>
            <p>Have some recipe feedback? Spotted a bug? Let us know.</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 @lg:flex-row">
          <button
            onClick={(e) => {
              setModalOpen(true);
            }}
            className="button button-dark justify-center font-sans"
          >
            Send recipe feedback
          </button>
          <button
            onClick={() => {
              if (YbugApi) {
                YbugApi.open("feedback");
              }
            }}
            data-ybug="open"
            className="button button-dark justify-center font-sans"
          >
            Report bug
          </button>
        </div>
      </div>
      <Modal
        isOpen={modalOpen}
        heading={"Let the chefs know your thoughts"}
        isClosable
        onRequestClose={() => {
          setModalOpen(false);
        }}
      >
        <div className="mt-6">
          {status === "initial" && (
            <>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submitFeedback();
                }}
              >
                <textarea
                  name="feedback"
                  className="mb-6 h-[200px] w-full rounded-md bg-zinc-200 p-6"
                  placeholder="Leave your feedback..."
                  value={feedback}
                  onChange={(e) => {
                    setFeedback(e.target.value);
                  }}
                  required
                />
                <Button
                  onClick={submitFeedback}
                  isDisabled={feedback == ""}
                  isFull
                  label={"Submit"}
                  isSmall={false}
                />
              </form>
              <div className="mt-6 text-center text-xs leading-snug opacity-70">
                <Balancer as="p">
                  By submitting this feedback, you are agreeing to our terms of
                  service and privacy policies.
                </Balancer>
              </div>
            </>
          )}
          {status === "submitting" && (
            <>
              <InlineLoading message="Sending feedback..." />
            </>
          )}
          {status === "complete" && (
            <div className="mt-6 text-center text-base leading-snug opacity-70">
              <Balancer as="p">
                Thanks. We have passed your feedback on to the chefs.
              </Balancer>
            </div>
          )}
          {status === "error" && (
            <div className="mt-6 text-center text-base leading-snug opacity-70">
              <Balancer as="p">
                Sorry. We weren&rsquo;t able to send your feedback. Please try
                again later.
              </Balancer>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default RecipeFeedbackBugWidget;
